import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  LogOut,
  Camera,
  Upload,
  ScanLine,
  MessageCircleHeart,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import BrandHeart from "../components/qhearts/BrandHeart";
import {
  getSessionProfileId,
  setSessionProfileId,
} from "../context/OnboardingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_MESSAGE_LEN = 300;
const GUEST_KEY = "qhearts-guest-id";
const GUEST_NAME_KEY = "qhearts-guest-name";

function getOrCreateGuestId() {
  try {
    let id = localStorage.getItem(GUEST_KEY);
    if (!id) {
      id = `guest-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
      localStorage.setItem(GUEST_KEY, id);
    }
    return id;
  } catch {
    return `guest-${Math.random().toString(36).slice(2)}`;
  }
}

function computeAge(birthDate) {
  if (!birthDate) return null;
  const parts = birthDate.split("-").map(Number);
  if (parts.length < 1 || !parts[0]) return null;
  const b = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1);
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const md = now.getMonth() - b.getMonth();
  if (md < 0 || (md === 0 && now.getDate() < b.getDate())) a--;
  return a;
}

// Profile field registry — which fields are toggleable onto the heart
const PROFILE_FIELDS = [
  { key: "photo", label: "Profilbild", essential: true },
  { key: "name", label: "Name", essential: true },
  { key: "age", label: "Alter" },
  { key: "city", label: "Stadt" },
  { key: "profession", label: "Beruf" },
  { key: "bio", label: "Kurzer Text über mich" },
  { key: "interests", label: "Interessen" },
  { key: "traits", label: "Persönlichkeit" },
  { key: "relationship_type", label: "Beziehungstyp" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "faith", label: "Glaube" },
];

function buildProfileSnapshot(profile, guestName) {
  if (profile) {
    const main = profile.photos?.[0];
    return {
      name: profile.name,
      photo_url: main?.url || null,
      age: computeAge(profile.birth_date),
      city: profile.city || null,
      profession: profile.profession || null,
      bio: profile.bio || null,
      interests: profile.interests || [],
      traits: profile.traits || [],
      relationship_type: profile.relationship_type || null,
      lifestyle: profile.lifestyle || null,
      faith: profile.faith || null,
    };
  }
  return {
    name: guestName || "Du",
    photo_url: null,
    age: null,
    city: null,
    profession: null,
    bio: null,
    interests: [],
    traits: [],
    relationship_type: null,
    lifestyle: null,
    faith: null,
  };
}

function fieldHasValue(snapshot, key) {
  if (!snapshot) return false;
  if (key === "photo") return !!snapshot.photo_url;
  if (key === "name") return !!snapshot.name;
  if (key === "age") return typeof snapshot.age === "number" && snapshot.age > 0;
  if (key === "interests" || key === "traits")
    return Array.isArray(snapshot[key]) && snapshot[key].length > 0;
  return !!snapshot[key];
}

const STATUS_META = {
  available: {
    label: "Verfügbar",
    className: "bg-[#FFE4E6] text-qh-rose",
    icon: Sparkles,
  },
  ready: {
    label: "Bereit zur Übergabe",
    className: "bg-qh-rose text-white",
    icon: Sparkles,
  },
  given: {
    label: "Offen",
    className: "bg-qh-ink/5 text-qh-ink",
    icon: Clock,
  },
  match: {
    label: "Match",
    className: "bg-qh-rose text-white",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Abgelehnt",
    className: "bg-qh-slate/10 text-qh-slate",
    icon: XCircle,
  },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.available;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.className}`}
      data-testid={`heart-status-${status}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {meta.label}
    </span>
  );
}

function HeartPreview({ message, snapshot, sharedFields, size = "lg" }) {
  const dims = size === "lg" ? "max-w-[360px]" : "max-w-[200px]";
  const shows = (k) => sharedFields.includes(k) && fieldHasValue(snapshot, k);
  const photoUrl = snapshot?.photo_url ? `${MEDIA_BASE}${snapshot.photo_url}` : null;
  const displayName = snapshot?.name || "Dein Name";

  return (
    <div className="w-full mx-auto">
      <div
        className={`relative mx-auto w-full aspect-square ${dims} rounded-[32px] bg-gradient-to-br from-[#FFF1F3] via-white to-[#FFE4E6] border border-qh-line overflow-hidden shadow-[0_30px_60px_-20px_rgba(225,29,72,0.25)]`}
        data-testid="heart-preview"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <BrandHeart className="w-[78%] h-[78%] opacity-95" alt="" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-between p-6 sm:p-8">
          <div className="flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3 py-1 border border-qh-line shadow-sm">
            {shows("photo") && photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <span className="w-6 h-6 rounded-full bg-[#FFE4E6] flex items-center justify-center text-[11px] font-semibold text-qh-rose">
                {displayName?.[0]?.toUpperCase() || "Q"}
              </span>
            )}
            <span className="text-xs font-semibold text-qh-ink">
              {shows("name") ? displayName : "Anonym"}
              {shows("age") ? `, ${snapshot.age}` : ""}
            </span>
          </div>

          <div className="w-full max-w-[80%] mx-auto text-center">
            <AnimatePresence mode="wait">
              <motion.p
                key={message || "empty"}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35 }}
                className="font-display italic text-[15px] sm:text-base leading-snug text-qh-ink/90"
              >
                {message ? `„${message}"` : "Deine Nachricht erscheint hier."}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Shared-field chips visible to the recipient */}
      {(shows("city") ||
        shows("profession") ||
        shows("relationship_type") ||
        shows("lifestyle") ||
        shows("faith") ||
        shows("traits") ||
        shows("interests") ||
        shows("bio")) && (
        <div
          className="mt-5 flex flex-wrap gap-1.5 justify-center"
          data-testid="heart-shared-chips"
        >
          {shows("city") && <MiniChip>{snapshot.city}</MiniChip>}
          {shows("profession") && <MiniChip>{snapshot.profession}</MiniChip>}
          {shows("relationship_type") && (
            <MiniChip>{snapshot.relationship_type}</MiniChip>
          )}
          {shows("lifestyle") && <MiniChip>{snapshot.lifestyle}</MiniChip>}
          {shows("faith") && <MiniChip>{snapshot.faith}</MiniChip>}
          {shows("traits") &&
            snapshot.traits.slice(0, 4).map((t) => (
              <MiniChip key={`t-${t}`} tone="rose">
                {t}
              </MiniChip>
            ))}
          {shows("interests") &&
            snapshot.interests.slice(0, 4).map((t) => (
              <MiniChip key={`i-${t}`}>{t}</MiniChip>
            ))}
        </div>
      )}
      {shows("bio") && (
        <p className="mt-4 text-center text-sm text-qh-slate italic max-w-sm mx-auto">
          „{snapshot.bio}"
        </p>
      )}
    </div>
  );
}

function MiniChip({ children, tone = "neutral" }) {
  const cls =
    tone === "rose"
      ? "bg-[#FFE4E6] text-qh-rose border-[#FFE4E6]"
      : "bg-white text-qh-ink border-qh-line";
  return (
    <span
      className={`text-[11px] px-2.5 py-1 rounded-full border ${cls}`}
    >
      {children}
    </span>
  );
}

function QRScanner({ onDetected, onClose }) {
  const elementId = "qh-qr-scanner-region";
  const scannerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let scanner = null;

    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        scanner = new Html5Qrcode(elementId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            onDetected(decoded);
          },
          () => {}
        );
      } catch (err) {
        toast.error("Kamera konnte nicht gestartet werden.", {
          description: "Erlaube den Kamera-Zugriff oder nutze den Upload.",
        });
      }
    })();

    return () => {
      cancelled = true;
      if (scannerRef.current) {
        scannerRef.current
          .stop()
          .then(() => scannerRef.current?.clear())
          .catch(() => {});
      }
    };
  }, [onDetected]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-5">
      <div className="bg-white rounded-3xl p-5 w-full max-w-md">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
              QR-Scanner
            </div>
            <h3 className="mt-1 font-display text-xl font-medium">
              Halte das QHeart in den Rahmen
            </h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-full border border-qh-line h-9 w-9 flex items-center justify-center"
            data-testid="qr-close"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
        <div
          id={elementId}
          className="mt-4 rounded-2xl overflow-hidden border border-qh-line aspect-square bg-black"
        />
        <p className="mt-3 text-xs text-qh-slate text-center">
          Wir lesen nur den Code — keine Bilder werden gespeichert.
        </p>
      </div>
    </div>
  );
}

async function decodeQRFromFile(file) {
  const { Html5Qrcode } = await import("html5-qrcode");
  const scanner = new Html5Qrcode("qh-qr-upload-region");
  try {
    const decoded = await scanner.scanFile(file, true);
    return decoded;
  } finally {
    try {
      await scanner.clear();
    } catch {
      /* noop */
    }
  }
}

function extractHeartCode(raw) {
  if (!raw) return null;
  const trimmed = raw.trim();
  // Accept plain codes like "QH-ABC123" or URL containing /claim/<code>
  const urlMatch = trimmed.match(/\/claim\/([A-Za-z0-9\-_]+)/);
  if (urlMatch) return urlMatch[1];
  return trimmed;
}

export default function Herzkammer() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [hearts, setHearts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeHeart, setActiveHeart] = useState(null);
  const [message, setMessage] = useState("");
  const [sharedFields, setSharedFields] = useState(["photo", "name"]);
  const [guestBio, setGuestBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  const [scannerOpen, setScannerOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const uploadRef = useRef(null);

  const sessionId = getSessionProfileId();
  const isGuest = !sessionId;
  const profileId = sessionId || getOrCreateGuestId();

  const [guestName, setGuestName] = useState(() => {
    try {
      return localStorage.getItem(GUEST_NAME_KEY) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (!isGuest) return;
    try {
      if (guestName) localStorage.setItem(GUEST_NAME_KEY, guestName);
    } catch {
      /* noop */
    }
  }, [guestName, isGuest]);

  const loadAll = useCallback(async () => {
    try {
      const tasks = [axios.get(`${API}/hearts`, { params: { owner_id: profileId } })];
      if (!isGuest) {
        tasks.unshift(axios.get(`${API}/profiles/${profileId}`));
      }
      const results = await Promise.all(tasks);
      if (!isGuest) {
        setProfile(results[0].data);
        setHearts(results[1].data);
      } else {
        setHearts(results[0].data);
      }
    } catch (e) {
      if (e?.response?.status === 404 && !isGuest) {
        setSessionProfileId(null);
        // Switch silently to guest mode instead of forcing login
        setProfile(null);
      } else {
        setError("LoveZone konnte nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }, [profileId, isGuest]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!activeHeart && hearts.length > 0) {
      const firstEditable =
        hearts.find((h) => h.status === "available") ||
        hearts.find((h) => h.status === "ready");
      if (firstEditable) {
        setActiveHeart(firstEditable);
        setMessage(firstEditable.message || "");
        if (firstEditable.shared_fields?.length) {
          setSharedFields(firstEditable.shared_fields);
        }
      }
    }
  }, [hearts, activeHeart]);

  const available = useMemo(
    () => hearts.filter((h) => h.status === "available" || h.status === "ready"),
    [hearts]
  );
  const editableAvailable = useMemo(
    () => hearts.filter((h) => h.status === "available"),
    [hearts]
  );
  const ready = useMemo(
    () => hearts.filter((h) => h.status === "ready"),
    [hearts]
  );
  const given = useMemo(
    () => hearts.filter((h) => !["available", "ready"].includes(h.status)),
    [hearts]
  );
  const matches = useMemo(
    () => hearts.filter((h) => h.status === "match"),
    [hearts]
  );

  const personalize = async () => {
    if (!activeHeart) {
      toast.error("Wähle zuerst ein Herz zum Personalisieren.");
      return;
    }
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error("Schreibe zuerst eine Nachricht.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/hearts/${activeHeart.id}/personalize`,
        { message: trimmed }
      );
      setHearts((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
      setActiveHeart(res.data);
      toast.success("Dieses Herz gehört jetzt dir.", {
        description: "Ein Moment. Eine Nachricht. Ein echtes Gefühl.",
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Speichern fehlgeschlagen."
      );
    } finally {
      setSaving(false);
    }
  };

  const activate = async () => {
    if (!activeHeart) {
      toast.error("Wähle zuerst ein Herz.");
      return;
    }
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error("Schreibe zuerst eine Nachricht.");
      return;
    }
    const snapshot = buildProfileSnapshot(profile, guestName);
    if (isGuest && guestBio) snapshot.bio = guestBio;
    // Only keep fields that actually have values
    const cleanFields = sharedFields.filter((k) => fieldHasValue(snapshot, k));
    setActivating(true);
    try {
      const res = await axios.post(
        `${API}/hearts/${activeHeart.id}/activate`,
        {
          message: trimmed,
          shared_fields: cleanFields,
          profile_snapshot: snapshot,
        }
      );
      setHearts((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
      setActiveHeart(res.data);
      toast.success("Dieses Herz gehört jetzt dir.", {
        description:
          "Bereit zur Übergabe — scanne das QHeart der Person, der du es geben willst.",
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Aktivieren fehlgeschlagen."
      );
    } finally {
      setActivating(false);
    }
  };

  const claimByCode = async (rawCode) => {
    const code = extractHeartCode(rawCode);
    if (!code) {
      toast.error("Kein gültiger QR-Code erkannt.");
      return;
    }
    setClaiming(true);
    try {
      const res = await axios.post(
        `${API}/hearts/claim`,
        { code },
        { params: { owner_id: profileId } }
      );
      const newHeart = res.data;
      setHearts((prev) => {
        const exists = prev.some((h) => h.id === newHeart.id);
        return exists ? prev : [newHeart, ...prev];
      });
      toast.success("Herz hinzugefügt.", {
        description: `Code ${newHeart.code} ist jetzt in deiner Kammer.`,
      });
      setScannerOpen(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Dieses Herz konnte nicht hinzugefügt werden."
      );
    } finally {
      setClaiming(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const decoded = await decodeQRFromFile(file);
      claimByCode(decoded);
    } catch {
      toast.error("In diesem Bild wurde kein QR-Code gefunden.");
    } finally {
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const logout = () => {
    setSessionProfileId(null);
    navigate("/");
  };

  const previewSnapshot = useMemo(() => {
    const snap = buildProfileSnapshot(profile, guestName);
    if (isGuest && guestBio) snap.bio = guestBio;
    return snap;
  }, [profile, guestName, guestBio, isGuest]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-qh-slate">
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> Deine Kammer öffnet
        sich…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-qh-slate">
        {error}
      </div>
    );
  }

  const charCount = message.length;
  const remaining = MAX_MESSAGE_LEN - charCount;

  return (
    <div
      className="min-h-screen bg-[#FFFBFB] font-body text-qh-ink pb-20"
      data-testid="herzkammer-page"
    >
      <Toaster richColors position="top-center" />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center shrink-0"
            data-testid="herzkammer-logo"
          >
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
          <div className="flex items-center gap-2">
            {isGuest ? (
              <>
                <Link
                  to="/login"
                  className="text-sm text-qh-slate hover:text-qh-ink"
                  data-testid="herzkammer-login"
                >
                  Anmelden
                </Link>
                <Link to="/onboarding">
                  <Button
                    className="ml-2 rounded-full bg-qh-rose hover:bg-qh-rosedark text-white h-10 px-5 text-sm font-medium"
                    data-testid="herzkammer-register"
                  >
                    Registrieren
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/profile"
                  className="text-sm text-qh-slate hover:text-qh-ink"
                >
                  Profil
                </Link>
                <button
                  onClick={logout}
                  className="ml-2 text-sm text-qh-slate hover:text-qh-ink inline-flex items-center gap-1.5"
                  data-testid="herzkammer-logout"
                >
                  <LogOut className="w-4 h-4" /> Abmelden
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
            LoveZone
          </span>
          <h1 className="mt-3 font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            Mach dein Herz{" "}
            <span className="italic text-qh-rose">persönlich.</span>
          </h1>
          <p className="mt-5 text-lg text-qh-slate leading-relaxed max-w-2xl">
            Hier gestaltest du deine QHearts persönlich — für echte Begegnungen
            im echten Leben.
          </p>

          {isGuest && (
            <div
              className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-qh-line bg-white/80 backdrop-blur px-5 py-4 max-w-2xl"
              data-testid="herzkammer-guest-banner"
            >
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-qh-rose shrink-0">
                <Sparkles className="w-3.5 h-3.5" /> Gast-Modus
              </span>
              <p className="text-sm text-qh-slate flex-1">
                Deine Herzen werden in diesem Browser gespeichert.{" "}
                <Link
                  to="/onboarding"
                  className="text-qh-rose font-medium hover:text-qh-rosedark"
                >
                  Registriere dich
                </Link>
                , damit dein Profilbild und Name automatisch auf dem Herz
                erscheinen.
              </p>
            </div>
          )}
        </motion.div>

        {/* Personalize */}
        <section
          className="mt-12 sm:mt-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start"
          data-testid="personalize-section"
        >
          <div className="rounded-3xl bg-white border border-qh-line p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl font-medium">
                  Deine Nachricht auf dem QHeart personalisieren
                </h2>
                <p className="mt-1.5 text-sm text-qh-slate">
                  Ein Moment. Eine Nachricht. Ein echtes Gefühl.
                </p>
              </div>
              {activeHeart && (
                <span className="text-[11px] uppercase tracking-wider font-semibold text-qh-slate px-2.5 py-1 rounded-full border border-qh-line">
                  {activeHeart.code}
                </span>
              )}
            </div>

            <label
              htmlFor="heart-message"
              className="mt-6 block text-sm font-medium text-qh-ink"
            >
              Deine Nachricht
            </label>
            <textarea
              id="heart-message"
              value={message}
              onChange={(e) =>
                setMessage(e.target.value.slice(0, MAX_MESSAGE_LEN))
              }
              placeholder="Schreibe deine persönliche Nachricht…"
              rows={5}
              disabled={!activeHeart}
              className="mt-2 w-full rounded-2xl border border-qh-line bg-[#FFFBFB] px-5 py-4 text-base leading-relaxed focus:outline-none focus:border-qh-rose focus:ring-2 focus:ring-qh-rose/20 resize-none disabled:opacity-60"
              data-testid="heart-message-input"
            />
            <div className="mt-1 flex items-center justify-between text-xs text-qh-slate">
              <span>{charCount} / {MAX_MESSAGE_LEN}</span>
              {remaining <= 30 && remaining >= 0 && (
                <span className="text-qh-rose">Noch {remaining} Zeichen</span>
              )}
            </div>

            {available.length > 1 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-qh-slate">
                  Verfügbare Herzen
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {available.map((h) => (
                    <button
                      key={h.id}
                      onClick={() => {
                        setActiveHeart(h);
                        setMessage(h.message || "");
                        if (h.shared_fields?.length) {
                          setSharedFields(h.shared_fields);
                        }
                      }}
                      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${
                        activeHeart?.id === h.id
                          ? "border-qh-rose bg-[#FFE4E6] text-qh-rose"
                          : "border-qh-line bg-white text-qh-slate hover:text-qh-ink"
                      }`}
                      data-testid={`select-heart-${h.id}`}
                    >
                      {h.status === "ready" && (
                        <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                      )}
                      {h.code}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={personalize}
              disabled={
                saving ||
                !activeHeart ||
                !message.trim() ||
                activeHeart.status !== "available"
              }
              variant="outline"
              className="mt-6 w-full sm:w-auto rounded-full border-qh-line h-12 px-8 text-sm font-medium"
              data-testid="personalize-save"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Speichern…
                </span>
              ) : (
                "Nur Entwurf speichern"
              )}
            </Button>

            {!activeHeart && (
              <p className="mt-4 text-sm text-qh-slate">
                Keine verfügbaren Herzen. Füge unten ein Herz per QR-Code hinzu.
              </p>
            )}
          </div>

          {/* Live preview */}
          <div className="rounded-3xl bg-gradient-to-br from-white to-[#FFFBFB] border border-qh-line p-6 sm:p-10">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
                Live-Vorschau
              </div>
              {activeHeart?.status === "ready" && (
                <StatusBadge status="ready" />
              )}
            </div>
            {isGuest && (
              <div className="mt-5 max-w-xs mx-auto">
                <label
                  htmlFor="guest-name"
                  className="block text-xs uppercase tracking-wider text-qh-slate text-center"
                >
                  Dein Anzeigename
                </label>
                <input
                  id="guest-name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value.slice(0, 40))}
                  placeholder="Dein Name"
                  className="mt-2 w-full h-11 rounded-full border border-qh-line bg-white px-5 text-center text-sm focus:outline-none focus:border-qh-rose focus:ring-2 focus:ring-qh-rose/20"
                  data-testid="guest-name-input"
                />
              </div>
            )}
            <div className="mt-6">
              <HeartPreview
                message={message}
                snapshot={previewSnapshot}
                sharedFields={sharedFields}
              />
            </div>
            <p className="mt-6 text-center text-sm text-qh-slate">
              So sieht dein Herz für die Person aus, die es bekommt.
            </p>
          </div>
        </section>

        {/* Profile fields selection */}
        {activeHeart && (
          <section
            className="mt-10 sm:mt-14 rounded-3xl bg-white border border-qh-line p-6 sm:p-8"
            data-testid="profile-fields-section"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="max-w-xl">
                <h2 className="font-display text-2xl font-medium">
                  Dein Profil auf dem QHeart anpassen
                </h2>
                <p className="mt-1.5 text-sm text-qh-slate">
                  Entscheide selbst, was die Person sehen darf, die dein Herz
                  bekommt. Nur das, was du auswählst, landet auf dem Herz.
                </p>
              </div>
              {!isGuest ? (
                <Link
                  to="/profile"
                  className="text-sm text-qh-rose hover:text-qh-rosedark font-medium whitespace-nowrap"
                >
                  Profil bearbeiten →
                </Link>
              ) : (
                <Link
                  to="/onboarding"
                  className="text-sm text-qh-rose hover:text-qh-rosedark font-medium whitespace-nowrap"
                >
                  Profil erstellen →
                </Link>
              )}
            </div>

            {isGuest && (
              <div className="mt-6">
                <label
                  htmlFor="guest-bio"
                  className="block text-xs uppercase tracking-wider text-qh-slate"
                >
                  Kurzer Text über dich (Bio)
                </label>
                <textarea
                  id="guest-bio"
                  value={guestBio}
                  onChange={(e) => setGuestBio(e.target.value.slice(0, 200))}
                  placeholder="Ein Satz, der dich beschreibt…"
                  rows={2}
                  className="mt-2 w-full rounded-2xl border border-qh-line bg-[#FFFBFB] px-5 py-3 text-sm leading-relaxed focus:outline-none focus:border-qh-rose focus:ring-2 focus:ring-qh-rose/20 resize-none"
                  data-testid="guest-bio-input"
                />
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              {PROFILE_FIELDS.map((f) => {
                const has = fieldHasValue(previewSnapshot, f.key);
                const isOn = sharedFields.includes(f.key);
                const disabled = !has;
                const toggle = () => {
                  if (disabled) return;
                  setSharedFields((prev) =>
                    prev.includes(f.key)
                      ? prev.filter((x) => x !== f.key)
                      : [...prev, f.key]
                  );
                };
                return (
                  <button
                    key={f.key}
                    onClick={toggle}
                    disabled={disabled}
                    className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border transition-all ${
                      isOn
                        ? "border-qh-rose bg-[#FFE4E6] text-qh-rose"
                        : disabled
                          ? "border-qh-line bg-[#FFFBFB] text-qh-slate/50 cursor-not-allowed"
                          : "border-qh-line bg-white text-qh-slate hover:text-qh-ink"
                    }`}
                    data-testid={`field-toggle-${f.key}`}
                  >
                    {isOn && <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />}
                    {f.label}
                    {disabled && (
                      <span className="text-[10px] uppercase tracking-wider text-qh-slate/70 ml-1">
                        leer
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-qh-slate">
              {sharedFields.filter((k) => fieldHasValue(previewSnapshot, k)).length} von{" "}
              {PROFILE_FIELDS.filter((f) => fieldHasValue(previewSnapshot, f.key)).length}{" "}
              verfügbaren Feldern sichtbar.
            </p>

            <Button
              onClick={activate}
              disabled={
                activating ||
                !activeHeart ||
                !message.trim() ||
                !["available", "ready"].includes(activeHeart.status)
              }
              className="mt-8 w-full sm:w-auto rounded-full bg-qh-rose hover:bg-qh-rosedark text-white h-14 px-10 text-base font-medium transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="heart-activate"
            >
              {activating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Aktivieren…
                </>
              ) : activeHeart?.status === "ready" ? (
                <>
                  <Sparkles className="w-5 h-5" /> Aktualisieren & bereit halten
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" /> Herz jetzt aktivieren und
                  vergeben
                </>
              )}
            </Button>
            <p className="mt-3 text-xs text-qh-slate">
              Nach dem Aktivieren ist das Herz bereit zur Übergabe — scanne den
              QR-Code der Person, der du es geben willst.
            </p>
          </section>
        )}

        {/* QR claim */}
        <section
          className="mt-14 sm:mt-20 rounded-3xl bg-white border border-qh-line p-6 sm:p-8"
          data-testid="qr-section"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h2 className="font-display text-2xl font-medium">
                Herz hinzufügen
              </h2>
              <p className="mt-1.5 text-sm text-qh-slate">
                Scanne den QR-Code deines physischen QHeart oder lade ein Bild
                hoch. Dieses Herz gehört dann dir.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Button
                onClick={() => setScannerOpen(true)}
                disabled={claiming}
                className="rounded-full bg-qh-ink hover:bg-qh-ink/90 text-white h-12 px-6 text-sm font-medium transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                data-testid="qr-open-scanner"
              >
                <Camera className="w-4 h-4" /> Scanner öffnen
              </Button>
              <Button
                onClick={() => uploadRef.current?.click()}
                variant="outline"
                disabled={claiming}
                className="rounded-full border-qh-line h-12 px-6 text-sm font-medium inline-flex items-center gap-2"
                data-testid="qr-upload"
              >
                <Upload className="w-4 h-4" /> Bild hochladen
              </Button>
              <input
                ref={uploadRef}
                type="file"
                accept="image/*"
                onChange={onUpload}
                className="hidden"
              />
            </div>
          </div>
          <div id="qh-qr-upload-region" className="hidden" />
          <div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-qh-line bg-[#FFFBFB] px-4 py-2 text-xs text-qh-slate">
            <ScanLine className="w-3.5 h-3.5 text-qh-rose" />
            Funktioniert mit jedem QHeart-QR aus dem Shop oder von einer
            Veranstaltung.
          </div>
        </section>

        {/* Dashboards */}
        <section className="mt-14 sm:mt-20 grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Meine Herzen */}
          <div
            className="rounded-3xl bg-white border border-qh-line p-6 sm:p-8"
            data-testid="my-hearts"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-2xl font-medium">
                Meine Herzen
              </h2>
              <span className="font-display text-4xl font-medium text-qh-rose">
                {available.length}
              </span>
            </div>
            <p className="mt-1.5 text-sm text-qh-slate">
              {ready.length > 0
                ? `${ready.length} bereit zur Übergabe.`
                : "Bereit, gestaltet zu werden."}
            </p>
            {available.length === 0 ? (
              <p className="mt-6 text-sm text-qh-slate">
                Noch keine verfügbaren Herzen. Füge oben eins hinzu.
              </p>
            ) : (
              <ul className="mt-5 space-y-3">
                {available.map((h) => (
                  <li
                    key={h.id}
                    className="flex items-center gap-4 rounded-2xl border border-qh-line bg-[#FFFBFB] p-4"
                    data-testid={`my-heart-${h.id}`}
                  >
                    <div className="h-12 w-12 rounded-2xl bg-[#FFE4E6] flex items-center justify-center shrink-0">
                      <BrandHeart className="w-7 h-7" alt="" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-wider text-qh-slate">
                        {h.code}
                      </div>
                      <div className="mt-0.5 text-sm text-qh-ink truncate">
                        {h.message ? `„${h.message}"` : "Noch nicht personalisiert"}
                      </div>
                    </div>
                    <StatusBadge status={h.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Vergebene Herzen */}
          <div
            className="rounded-3xl bg-white border border-qh-line p-6 sm:p-8"
            data-testid="given-hearts"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-2xl font-medium">
                Vergebene Herzen
              </h2>
              <span className="font-display text-4xl font-medium text-qh-ink">
                {given.length}
              </span>
            </div>
            <p className="mt-1.5 text-sm text-qh-slate">
              Deine Spur durch echte Begegnungen.
            </p>
            {given.length === 0 ? (
              <p className="mt-6 text-sm text-qh-slate">
                Noch keine Herzen vergeben — der erste Moment wartet.
              </p>
            ) : (
              <ul className="mt-5 space-y-3">
                {given.map((h) => (
                  <li
                    key={h.id}
                    className="flex items-center gap-4 rounded-2xl border border-qh-line bg-[#FFFBFB] p-4"
                    data-testid={`given-heart-${h.id}`}
                  >
                    {h.recipient_photo_url ? (
                      <img
                        src={`${MEDIA_BASE}${h.recipient_photo_url}`}
                        alt={h.recipient_name || ""}
                        className="h-12 w-12 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-[#FFE4E6] flex items-center justify-center shrink-0">
                        <BrandHeart className="w-6 h-6" alt="" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-qh-ink truncate">
                        {h.recipient_name || "Unbekannt"}
                      </div>
                      <div className="mt-0.5 text-xs text-qh-slate truncate">
                        {h.message ? `„${h.message}"` : h.code}
                      </div>
                    </div>
                    <StatusBadge status={h.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Matches */}
        {matches.length > 0 && (
          <section
            className="mt-14 sm:mt-20 rounded-3xl bg-gradient-to-br from-[#FFE4E6] to-white border border-qh-line p-6 sm:p-10"
            data-testid="matches-section"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-qh-rose flex items-center justify-center">
                <MessageCircleHeart
                  className="w-5 h-5 text-white"
                  strokeWidth={2}
                />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-medium">
                Match entstanden.
              </h2>
            </div>
            <p className="mt-3 text-qh-slate max-w-xl">
              Aus einem Herz wurde ein echter Moment. Jetzt ist der Augenblick,
              das Gespräch zu beginnen.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {matches.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-4 rounded-2xl bg-white border border-qh-line p-4"
                  data-testid={`match-${h.id}`}
                >
                  {h.recipient_photo_url ? (
                    <img
                      src={`${MEDIA_BASE}${h.recipient_photo_url}`}
                      alt={h.recipient_name || ""}
                      className="h-14 w-14 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-[#FFE4E6] flex items-center justify-center shrink-0">
                      <BrandHeart className="w-7 h-7" alt="" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-qh-ink">
                      {h.recipient_name || "Dein Match"}
                    </div>
                    <div className="text-xs text-qh-slate truncate">
                      {h.message ? `„${h.message}"` : h.code}
                    </div>
                  </div>
                  <Button
                    onClick={() =>
                      toast("Chat kommt bald.", {
                        description:
                          "Wir öffnen den Raum zum Schreiben in Kürze.",
                      })
                    }
                    className="rounded-full bg-qh-rose hover:bg-qh-rosedark text-white h-10 px-4 text-xs font-medium inline-flex items-center gap-1.5"
                    data-testid={`match-chat-${h.id}`}
                  >
                    Zum Chat <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {scannerOpen && (
        <QRScanner
          onDetected={(code) => claimByCode(code)}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </div>
  );
}
